import { DataTable, dateBetweenFilterFn } from '@porsche-code/kado-ui';
import { createFileRoute } from '@tanstack/react-router';
import { createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { DateOutput } from '@/components';
import { NAMESPACES } from '@/config/i18n';
import { useMovedOrganizationsQuery } from '@/graphql';

export const Route = createFileRoute(
  '/admin/ppn-masterdata-import/moved-organizations',
)({
  component: MovedOrganizations,
  beforeLoad: () => {
    return {
      breadcrumb: () => (
        <Trans i18nKey="admin.ppnMasterdataImport.movedOrganizations" />
      ),
    };
  },
});

function MovedOrganizations() {
  const { t } = useTranslation(NAMESPACES);

  const { data: organizations, isPending } = useMovedOrganizationsQuery(
    {
      filter: {
        previousId: { _exists: true },
      },
      limit: 0,
    },
    {
      select: (data) =>
        data.organizations.docs.map((org) => ({
          ...org,
          id: org.id.toString(),
          previous: {
            ...org.previous,
            id: org.previous?.id.toString(),
          },
        })),
    },
  );

  const columns = useMemo(() => {
    const columnHelper =
      createColumnHelper<NonNullable<typeof organizations>[0]>();

    return [
      columnHelper.accessor('id', {
        header: () => t('common:id'),
      }),
      columnHelper.accessor('partnerNumber', {
        header: () => t('common:partnerNumber'),
      }),
      columnHelper.accessor('name', {
        cell: (data) => `${data.getValue()}`,
        header: () => t('common:organization'),
      }),
      columnHelper.accessor('createdAt', {
        cell: (data) => {
          const value = data.getValue();

          return value ? <DateOutput date={value} /> : null;
        },
        header: () => t('admin.ppnMasterdataImport.createdAt'),
        filterFn: dateBetweenFilterFn,
        meta: {
          filterType: 'date',
        },
      }),
      columnHelper.accessor('previous.id', {
        header: () => t('admin.ppnMasterdataImport.previousId'),
      }),
      columnHelper.accessor('previous.partnerNumber', {
        header: () => t('admin.ppnMasterdataImport.previousPartnerNumber'),
      }),
      columnHelper.accessor('previous.name', {
        header: () => t('admin.ppnMasterdataImport.previousName'),
      }),
    ];
  }, [t]);

  return (
    <DataTable
      caption={t('featureArticles')}
      isLoading={isPending}
      defaultSort={[{ desc: true, id: 'createdAt' }]}
      data={organizations ?? []}
      columns={columns}
      idAccessor="id"
      i18n={{
        filterLabel: (columnName) =>
          t('common:iconLabel.filter', { columnName }),
        optionAll: t('common:all'),
        buttonReset: t('common:action.reset'),
        buttonFilter: t('common:action.filter'),
        actionSearch: t('common:action.search'),
        noData: t('common:noData'),
        minLabel: t('common:min'),
        maxLabel: t('common:max'),
      }}
    />
  );
}
